import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { cdnClient, LoadingState, logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { forwardRef, lazy, Suspense, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { isHlsMediaType, isSupportedMediaType } from '../../utils/media-type-utils';
import { Button } from '../button/button';
import { ButtonColor } from '../button/types/color';
import { ButtonSize } from '../button/types/size';
import { Message } from '../message';
import { DefaultVideoPlayer } from './subcomponents/default-video-player';
const LazyLoadedHlsVideoPlayer = lazy(() => import('./subcomponents/hls-video-player'));
const b = bemClassBuilder('v-video-player');
export const VideoPlayer = forwardRef((props, ref) => {
    const { src, className, events, displayText, assets } = props, videoProps = __rest(props, ["src", "className", "events", "displayText", "assets"]);
    const { onVideoLoaded, onVideoFailedToLoad, onVideoError, onRetryButtonClicked } = events;
    const [requiresHlsPlayer, setRequiresHlsPlayer] = useState(false);
    const [loadingState, setLoadingState] = useState(LoadingState.Loading);
    const videoPlayerRef = useRef(null);
    const loadVideo = useCallback(() => {
        (async () => {
            try {
                if (!src)
                    throw new Error('Cannot render the VideoPlayer component without a src URL.');
                setLoadingState(LoadingState.Loading);
                const cdnMediaType = await cdnClient.instance.getCdnMediaType(src);
                if (cdnMediaType && !isSupportedMediaType(cdnMediaType))
                    throw new Error('unsupported media type');
                // Safari supports HTTP Live Streaming by default - https://developer.apple.com/documentation/http_live_streaming
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                setRequiresHlsPlayer(isHlsMediaType(cdnMediaType) && !isSafari);
                setLoadingState(LoadingState.Success);
                onVideoLoaded === null || onVideoLoaded === void 0 ? void 0 : onVideoLoaded();
            }
            catch (error) {
                setLoadingState(LoadingState.Failure);
                onVideoFailedToLoad === null || onVideoFailedToLoad === void 0 ? void 0 : onVideoFailedToLoad();
                logger.error(`Failed to load CDN media type, ${error.message}`);
            }
        })();
    }, [src, onVideoLoaded, onVideoFailedToLoad]);
    useEffect(loadVideo, [loadVideo]);
    const play = useCallback(() => { var _a; return (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.play(); }, []);
    const pause = useCallback(() => { var _a; return (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.pause(); }, []);
    const load = useCallback(() => loadVideo(), [loadVideo]);
    const setTime = useCallback((time) => {
        if (videoPlayerRef.current) {
            videoPlayerRef.current.currentTime = time;
        }
    }, []);
    useImperativeHandle(ref, () => {
        return {
            play,
            pause,
            load,
            setTime
        };
    });
    const handleFailedToLoad = useCallback(() => {
        setLoadingState(LoadingState.Failure);
        onVideoFailedToLoad === null || onVideoFailedToLoad === void 0 ? void 0 : onVideoFailedToLoad();
    }, [onVideoFailedToLoad]);
    const handleError = useCallback(() => {
        var _a, _b;
        setLoadingState(LoadingState.Failure);
        onVideoError === null || onVideoError === void 0 ? void 0 : onVideoError((_b = (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : undefined);
    }, [onVideoError]);
    const playerSkeleton = _jsx(VideoPlayerSkeleton, Object.assign({}, videoProps));
    if (loadingState === LoadingState.Loading)
        return playerSkeleton;
    if (loadingState === LoadingState.Failure) {
        return (_jsx(Message, Object.assign({ iconName: assets.failedToLoadIconName, title: displayText.failedToLoadTitle, className: 'v-video-player-message' }, { children: _jsx(Button, Object.assign({ className: b.element('retry-button'), onClick: onRetryButtonClicked, color: ButtonColor.Primary, size: ButtonSize.Large }, { children: displayText.failedToLoadRetryButtonLabel })) })));
    }
    return (_jsx("div", Object.assign({ className: b.block(undefined, className) }, { children: requiresHlsPlayer ? (_jsx(Suspense, Object.assign({ fallback: playerSkeleton }, { children: _jsx(LazyLoadedHlsVideoPlayer, Object.assign({}, videoProps, { src: src, onFailedToLoad: handleFailedToLoad, onError: handleError, ref: videoPlayerRef })) }))) : (_jsx(DefaultVideoPlayer, Object.assign({}, videoProps, { src: src, onError: handleError, ref: videoPlayerRef }))) })));
});
VideoPlayer.displayName = 'VideoPlayer';
export const VideoPlayerSkeleton = (_a) => {
    var { className, onError } = _a, props = __rest(_a, ["className", "onError"]);
    return (_jsx(DefaultVideoPlayer, Object.assign({}, props, { className: b.block({ skeleton: true }, className), src: '' })));
};
VideoPlayerSkeleton.displayName = 'VideoPlayerSkeleton';
